import React from 'react';
import { Link } from 'react-router-dom';
import { Note, Steps, scrollToSection } from './wq';
import { PAYMENT_METHODS } from './paymentMethods';

const COPY = {
    web: {
        lead: (
            <>
                Accept payments on your website or web app. Choose the plugin or SDK for your stack below — or, if
                you'd rather call the Merchant API yourself, use the{' '}
                <Link to="/standard-api-integration" className="wq-link">
                    Standard API Integration
                </Link>
                .
            </>
        ),
        start: 'Your server starts a payment with your Merchant ID and Secret Key.',
    },
    mobile: {
        lead: 'Accept payments inside your iOS or Android app with the WayaQuick mobile SDKs below. Each SDK wraps the same Merchant API, so payments, payouts and verification work the same way on every platform.',
        start: 'Your app or backend starts a payment with your Merchant ID and Secret Key.',
    },
};

/**
 * Opening section for the Web and Mobile integration pages: how a payment
 * works, the payment methods customers can use, the exact-amount rule, and
 * how to try the checkout.
 */
function IntegrationOverview({ id, platform = 'web', apiKeysSectionId }) {
    const copy = COPY[platform] ?? COPY.web;

    const flow = [
        copy.start,
        'WayaQuick returns a secure checkout link. Send your customer to it.',
        'The customer pays with any payment method enabled on your account.',
        'Confirm the payment with a webhook or a status check before you deliver the order.',
    ];

    return (
        <section className="wq-section" id={id}>
            <h2 className="wq-h2">Overview</h2>
            <p className="wq-lead">{copy.lead}</p>

            <h4 className="wq-h4">Before you start</h4>
            <p className="wq-p">
                You'll need a WayaQuick merchant account and your API keys.{' '}
                <button type="button" className="wq-link-btn" onClick={() => scrollToSection(apiKeysSectionId)}>
                    See how to get your API keys
                </button>
                .
            </p>

            <h4 className="wq-h4">How a payment works</h4>
            <Steps items={flow} />

            <h4 className="wq-h4">Payment methods</h4>
            <p className="wq-p">
                Customers can pay on the WayaQuick checkout with any of these methods:
            </p>
            <div className="wq-methods">
                {PAYMENT_METHODS.map((method) => (
                    <div key={method.name} className="wq-method-card">
                        <div className="wq-method-card-title">{method.name}</div>
                        <div className="wq-method-card-desc">{method.description}</div>
                    </div>
                ))}
            </div>
            <p className="wq-p">
                Customers only see the methods enabled for your account. Manage them in your dashboard under{' '}
                <strong>Settings › WayaQuick Settings › Payment Preferences</strong>.
            </p>

            <Note label="Exact amounts only" tone="critical">
                Customers must pay the exact amount on the payment link — WayaQuick does not accept part payments or
                overpayments. If a customer pays a different amount, more or less, the payment is rejected
                automatically and not credited to you. If the money has already come through, it is reversed to the
                customer automatically.
            </Note>

            <p className="wq-p">Need to collect money differently?</p>
            <ul className="wq-p wq-list">
                <li>
                    <strong>A smaller amount:</strong> create a payment link for exactly that amount.
                </li>
                <li>
                    <strong>In instalments:</strong> create a subscription link. Divide the total by the number of
                    payments yourself and use the result as the link amount — for example, ₦120,000 over 4 payments is
                    ₦30,000, debited 4 times.
                </li>
            </ul>

            <h4 className="wq-h4">Try the checkout</h4>
            <p className="wq-p">
                See what your customers will see with the{' '}
                <a href="https://pay.wayaquick.com/demo" target="_blank" rel="noreferrer" className="wq-link">
                    live checkout demo
                </a>
                .
            </p>
            <Note label="Real money">
                The demo processes real payments and debits your account, so use a small amount. To test without
                moving money, use your test secret key with the{' '}
                <Link to="/test-card-information" className="wq-link">
                    test cards
                </Link>
                .
            </Note>
        </section>
    );
}

export default IntegrationOverview;
